// third party imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Select, message } from "antd";
import { X, ChevronDown } from "react-feather";

// internal imports
import "./stylesSettleBetPopup.css";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
  handleExpiredTokenError,
} from "../../../../../utils/ErrorUtils";
import { AppStrings } from "../../../../../utils/constants/AppStrings";

const { Option } = Select;

const SETTLEMENT_STATUS_OPTIONS = [
  {
    value: "unsettled",
    label: "Unsettled",
    color: "#f59e0b",
    textColor: "#000",
  },
  { value: "won", label: "Won", color: "#22c55e", textColor: "#fff" },
  { value: "lost", label: "Lost", color: "#ef4444", textColor: "#fff" },
  { value: "void", label: "Void", color: "#6b7280", textColor: "#fff" },
];

// Status mapping: won=1, lost=2, void=4, unsettled=5
const STATUS_MAP = {
  won: 1,
  lost: 2,
  void: 4,
  unsettled: 5,
};

const SettleBetPopup = ({ isVisible, onClose, betData, refreshBets }) => {
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [legs, setLegs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLegStatus = (leg) => {
    if (!leg.finalized) return "unsettled";
    if (leg.void_factor === "0") return "void";
    if (leg.won === "1") return "won";
    if (leg.won === "-1") return "unsettled";
    if (leg.won === "0") return "lost";
  };

  // Initialize legs from bet data
  useEffect(() => {
    if (betData?.legs) {
      setLegs(
        betData.legs.map((leg, index) => ({
          ...leg,
          bet_index: index,
          status: handleLegStatus(leg),
        }))
      );
    } else if (betData?.selections) {
      // Create legs from selections if legs not provided
      setLegs(
        betData.selections.map((sel, index) => ({
          void_factor: betData?.bets[index]?.void_factor || null,
          market: betData.markets?.[index] || "Market",
          selection: sel.name || "Selection",
          status: handleLegStatus(betData?.bets[index]),
          bet_index: index,
          finalized: betData?.bets[index]?.finalized || false,
        }))
      );
    }
  }, [betData]);

  const handleStatusChange = (legIndex, newStatus) => {
    setLegs((prevLegs) =>
      prevLegs.map((leg, idx) =>
        idx === legIndex ? { ...leg, status: newStatus } : leg
      )
    );
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      // Create individual payloads for each leg
      const legPayloads = legs.map((leg) => {
        const payload = {
          bet_id: betData?.bet_id,
          bet_index: leg.bet_index,
          status: STATUS_MAP[leg.status] || STATUS_MAP.unsettled,
          finalized: leg.finalized,
        };
        // Add void_factor when status is void
        if (leg.status === "void") {
          payload.void_factor = leg.void_factor;
        }
        return payload;
      });

      // Call the endpoint for each leg (in succession)
      let response;
      for (const payload of legPayloads) {
        // Skip unsettled legs (status 5)
        if (payload.status === 5 || payload.finalized) continue;
        
        // Remove finalized from the payload
        const { finalized, ...payloadWithoutFinalized } = payload;
        
        response = await PrivateAdminPanelInstance.post(
          API_ROUTES?.SETTLE_PIKKEM_BET,
          payloadWithoutFinalized
        );
      }

      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED
        )
      );
      refreshBets?.();
      onClose();
    } catch (error) {
      console.error("Error settling bet:", error);
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
      );
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusOption = (status) => {
    return (
      SETTLEMENT_STATUS_OPTIONS.find(
        (opt) => opt.value === status?.toLowerCase()
      ) || SETTLEMENT_STATUS_OPTIONS[0]
    );
  };

  if (!isVisible) return null;

  return (
    <div className="settle-bet-overlay">
      <div className="settle-bet-popup">
        {/* Header */}
        <div className="settle-bet-header">
          <h2 className="settle-bet-title">{AppStrings.SETTLE_BET}</h2>
          <button className="settle-close-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Bet ID */}
        <div className="settle-bet-id-row">
          <span className="settle-bet-id-label">{AppStrings.BET_ID}</span>
          <span className="settle-bet-id-value">{betData?.bet_id || "-"}</span>
        </div>

        {/* Legs */}
        <div className="settle-legs-container">
          {legs.map((leg, index) => {
            return (
              <div key={index} className={`settle-leg-row ${leg.finalized ? "settle-leg-row-finalized" : ""}`}>
                <div className="settle-leg-info">
                  <span className="settle-leg-label">
                    {AppStrings.LEG} {leg.bet_index + 1}
                  </span>
                  <div className="settle-leg-details">
                    <span className="settle-leg-market">{leg.market}</span>
                    <span className="settle-leg-selection">
                      {leg.selection}
                    </span>
                  </div>
                </div>
                <div className="settle-leg-status">
                  <span className="settle-status-label">
                    {AppStrings.SETTLEMENT_STATUS}
                  </span>
                  <Select
                    value={leg.status}
                    onChange={(value) => handleStatusChange(index, value)}
                    className="settle-status-select"
                    popupClassName="settle-status-dropdown"
                    suffixIcon={<ChevronDown size={16} color="#fff" />}
                    disabled={leg.finalized}
                  >
                    {SETTLEMENT_STATUS_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        <div className="settle-status-option-wrapper">
                          <span
                            className="settle-status-option-badge"
                            style={{
                              backgroundColor: option.color,
                              color: option.textColor,
                            }}
                          >
                            {option.label}
                          </span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="settle-bet-actions">
          <button
            className="settle-cancel-button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="settle-save-button"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettleBetPopup;
